/**
 * Profiles — Netflix-style "Who's watching?" feature.
 * Profiles are now synced via the backend: they belong to a playlist, so any
 * activation key assigned that playlist sees the same profile set.
 *
 * The active profile id and a cached snapshot of profiles are kept in
 * localStorage so synchronous helpers (filterForKids, getActiveProfile, ...)
 * keep working from anywhere in the player tree.
 */
import {
  listProfilesApi,
  createProfileApi,
  updateProfileApi,
  deleteProfileApi,
  verifyProfilePinApi,
} from "./playerApi";

const PROFILES_CACHE_KEY = "vupp_profiles_cache";
const ACTIVE_PROFILE_KEY = "vupp_active_profile";

export const MAX_PROFILES = 5;

// Preset avatar colours — picked by index when creating a profile.
export const AVATAR_COLORS = [
  { from: "from-orange-500", to: "to-pink-500" },
  { from: "from-fuchsia-500", to: "to-purple-600" },
  { from: "from-sky-500", to: "to-indigo-600" },
  { from: "from-emerald-500", to: "to-teal-600" },
  { from: "from-amber-500", to: "to-orange-600" },
  { from: "from-rose-500", to: "to-red-600" },
  { from: "from-violet-500", to: "to-fuchsia-600" },
];

// ---------- cache ----------
function writeCache(list) {
  try {
    localStorage.setItem(PROFILES_CACHE_KEY, JSON.stringify(list || []));
    // v1.3.36 — debounced profile-cache sync to backend (best effort).
    // Lazy import avoids a circular-import problem (profileCacheSync
    // imports listProfiles from this file).
    import("./profileCacheSync")
      .then((m) => m.scheduleProfileCacheSync && m.scheduleProfileCacheSync())
      .catch(() => {});
  } catch {
    /* ignore */
  }
}

export function listProfiles() {
  try {
    const raw = localStorage.getItem(PROFILES_CACHE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

// ---------- active profile ----------
export function getActiveProfileId() {
  return localStorage.getItem(ACTIVE_PROFILE_KEY) || null;
}

export function getActiveProfile() {
  const id = getActiveProfileId();
  if (!id) return null;
  return listProfiles().find((p) => p.id === id) || null;
}

export function setActiveProfile(id) {
  if (id) localStorage.setItem(ACTIVE_PROFILE_KEY, id);
  else localStorage.removeItem(ACTIVE_PROFILE_KEY);
}

export function clearActiveProfile() {
  setActiveProfile(null);
}

// ---------- backend-backed API ----------
export async function refreshProfiles(playlistId) {
  const list = await listProfilesApi(playlistId);
  writeCache(list);
  return list;
}

export async function createProfile({ name, avatarIndex = 0, isKids = false, pin = "", playlistId }) {
  if (!playlistId) throw new Error("Active playlist is required to create a profile");
  const list = listProfiles().filter((p) => p.playlist_id === playlistId);
  if (list.length >= MAX_PROFILES) {
    throw new Error(`You can only have up to ${MAX_PROFILES} profiles.`);
  }
  const trimmed = (name || "").trim();
  if (!trimmed) throw new Error("Profile name is required");
  const cleanPin = (pin || "").replace(/\D/g, "");
  if (cleanPin && cleanPin.length !== 4) {
    throw new Error("PIN must be 4 digits");
  }
  const created = await createProfileApi({
    playlist_id: playlistId,
    name: trimmed.slice(0, 24),
    avatar_index: avatarIndex % AVATAR_COLORS.length,
    is_kids: !!isKids,
    pin: !isKids && cleanPin ? cleanPin : null,
  });
  await refreshProfiles(); // re-sync cache across all playlists
  return created;
}

export async function deleteProfile(id) {
  await deleteProfileApi(id);
  if (getActiveProfileId() === id) setActiveProfile(null);
  await refreshProfiles();
}

export async function setProfilePin(id, pin) {
  const cleanPin = (pin || "").replace(/\D/g, "");
  if (cleanPin && cleanPin.length !== 4) {
    throw new Error("PIN must be 4 digits");
  }
  await updateProfileApi(id, { pin: cleanPin || "" });
  await refreshProfiles();
}

export async function clearProfilePin(id) {
  await setProfilePin(id, "");
}

export async function verifyProfilePin(profileId, pin) {
  try {
    const r = await verifyProfilePinApi(profileId, pin);
    return !!r.valid;
  } catch {
    return false;
  }
}

export function hasPin(profile) {
  return !!(profile && profile.has_pin);
}

// === Kids mode content safety ===
const ADULT_KEYWORDS = [
  "adult", "xxx", "porn", "erotic", "erotica", "mature",
  "18+", "+18", "r-18", "r18", "hardcore", "sex ", " sex",
  "nsfw", "playboy", "hustler", "brazzers", "vivid", "for adults",
];

export function isKidsProfile(p) {
  return !!(p && p.is_kids);
}

export function isKidsActive() {
  const p = getActiveProfile();
  return isKidsProfile(p);
}

export function isAdultText(text) {
  if (!text) return false;
  const t = String(text).toLowerCase();
  return ADULT_KEYWORDS.some((kw) => t.includes(kw));
}

export function isAdultItem(item) {
  if (!item) return false;
  return isAdultText(item.group) || isAdultText(item.name);
}

export function filterForKids(items, opts = {}) {
  if (!isKidsActive()) return items;
  const keyName = opts.keyName || "name";
  const keyGroup = opts.keyGroup || "group";
  return items.filter(
    (it) => !isAdultText(it && it[keyGroup]) && !isAdultText(it && it[keyName])
  );
}
